import baseApiSlice from 'api/baseApiSlice';

import {
  type RemoteConfirmationStoreResponse,
  type RemoteConfirmationResponse,
  type UpdateRemoteConfirmationDonationStatusesPayload,
  type UpdateRemoteConfirmationDonationStatusesResponse,
} from './types';

type RemoteConfirmationTagTypes = {
  PendingDonations: 'PendingDonations';
  Stores: 'Stores';
};

type RemoteConfirmationTypesConstraints<Type> = {
  [Property in keyof Type]: Type[Property];
};

const RemoteConfirmationTags: RemoteConfirmationTypesConstraints<RemoteConfirmationTagTypes> = {
  PendingDonations: 'PendingDonations',
  Stores: 'Stores',
};

const remoteConfirmationAPISlice = baseApiSlice
  .enhanceEndpoints({
    addTagTypes: Object.values(RemoteConfirmationTags),
  })
  .injectEndpoints({
    endpoints: (build) => ({
      getRemoteConfirmationPendingDonations: build.query<RemoteConfirmationResponse, void>({
        query: () => ({
          url: '/charity/donations/v1/pending?size=100',
          method: 'GET',
        }),
        providesTags: [RemoteConfirmationTags.PendingDonations],
      }),
      getRemoteConfirmationStores: build.query<RemoteConfirmationStoreResponse, void>({
        query: () => ({
          url: '/charity/donations/v1/pending/stores',
          method: 'GET',
        }),
        providesTags: [RemoteConfirmationTags.Stores],
      }),
      updateRemoteConfirmationDonationStatuses: build.mutation<
        UpdateRemoteConfirmationDonationStatusesResponse,
        UpdateRemoteConfirmationDonationStatusesPayload
      >({
        query: (payload) => ({
          url: '/charity/donations/v1/collection-status',
          method: 'POST',
          data: payload,
        }),
        async onQueryStarted(_payload, { dispatch, queryFulfilled }) {
          try {
            const { data } = await queryFulfilled;
            const successfulOrderIds = new Set(
              data.results.filter((r) => r.success).map((r) => r.orderId),
            );
            if (successfulOrderIds.size === 0) return;
            dispatch(
              remoteConfirmationAPISlice.util.updateQueryData(
                'getRemoteConfirmationPendingDonations',
                undefined,
                (draft) => {
                  draft.donations.forEach((group) => {
                    group.donationItems = group.donationItems.filter(
                      ({ orderId }) => !successfulOrderIds.has(orderId),
                    );
                  });
                  draft.donations = draft.donations.filter(
                    ({ donationItems }) => donationItems.length > 0,
                  );
                },
              ),
            );
          } catch {
            dispatch(
              remoteConfirmationAPISlice.util.invalidateTags([
                RemoteConfirmationTags.PendingDonations,
              ]),
            );
          }
        },
      }),
    }),
  });

export default remoteConfirmationAPISlice;
